import React, { useState } from 'react';
import { Box, Button, Stack } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { paths } from 'routes';
import { useAuth } from 'lib/auth';
import { RedMessageBar } from 'components/RedMessageBar';
import { AuthLayout } from './AuthLayout';
import { CompanyInfoForm } from './CompanyInfoForm';
import { membershipIdForVat, toVat } from '../api/register';

export const JOIN_MODE_REQUEST = 'request';

const latestProfile = (account) =>
  [...(account?.users ?? [])].sort((a, b) => (b.user_id ?? 0) - (a.user_id ?? 0))[0];

export const JoinCompany = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { account, checkExistance, register, refreshSession } = useAuth();
  const requestMode = state?.mode === JOIN_MODE_REQUEST;
  const profile = latestProfile(account);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [values, setValues] = useState({
    firstName: profile?.first_name ?? '',
    lastName: profile?.last_name ?? '',
    phoneNumber: profile?.phone_number ?? '',
    companyName: '',
    country: 'Sweden',
    vat: '',
  });

  const handleChange = (input) => (e) => {
    setValues({ ...values, [input]: e.target.value });
  };

  const fail = (message) => {
    setError(message);
    setAttempt((current) => current + 1);
  };

  const requestAccess = () => navigate(paths.requestAccess, { state: { values } });

  const nextStep = async () => {
    setError('');
    try {
      const existance = await checkExistance(account.email, toVat(values.vat));
      if (existance?.company_exists && !existance.company_is_shadow) {
        requestAccess();
        return;
      }

      if (requestMode && !existance?.company_exists) {
        fail(t('website:auth.joinCompany.companyNotFound'));
        return;
      }

      const registered = await register(values, false);
      await refreshSession(membershipIdForVat(registered?.data, values.vat));
      navigate(paths.homepage);
    } catch (error) {
      if (error.code === 'company_exists') {
        requestAccess();
        return;
      }
      fail(error.message);
    }
  };

  return (
    <AuthLayout title={t('website:auth.membershipPending.pageTitle')}>
      <Box display="flex" flexDirection="column" alignItems="center" px={{ xs: 2, sm: 1 }} sx={{ mt: 5 }}>
        {error && (
          <Box width={{ xs: 1, sm: 368 }} sx={{ mb: 2 }}>
            <RedMessageBar message={error} />
          </Box>
        )}
        <CompanyInfoForm
          key={attempt}
          nextStep={nextStep}
          handleChange={handleChange}
          values={values}
          title={t(requestMode ? 'website:auth.joinCompany.requestTitle' : 'website:auth.joinCompany.registerTitle')}
          submitLabel={t(requestMode ? 'website:auth.joinCompany.continueButton' : 'website:auth.joinCompany.registerButton')}
        />
        <Stack width={{ xs: 1, sm: 368 }}>
          <Button variant="text" onClick={() => navigate(paths.homepage)}>
            {t('website:auth.joinCompany.backButton')}
          </Button>
        </Stack>
      </Box>
    </AuthLayout>
  );
};
